/**
 * Spatial Data API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound SpatialDataApi.
 * Each tool maps to a method on the C# SpatialDataApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (markdown-formatted, not JSON)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface SpatialDataApiToolSpec {
  /** Method name on the JS-side spatialDataApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  deferred?: boolean;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: SpatialDataApiToolSpec[] = [
  // ── Summary by ID ─────────────────────────────────────────────────────
  {
    method: "getSpatialSummary",
    name: "WS-spatialDataApi-getSpatialSummary",
    description:
      "Use this tool for questions about the selected feature's length, area, perimeter, size, extent, bounds, geometry, or point count, including phrases such as 'how long is this?'. " +
      "First read the selected featureId from get_run_context, then pass that numeric ID to this tool. " +
      "It returns actual measurements from the current Quadri model; do not substitute documentation or knowledge-base search for this call. " +
      "Returns a compact markdown summary of all spatial attributes for the feature. " +
      "Includes geometry type, point counts, lengths/areas, and bounding box. " +
      "This is the cheapest spatial call (~50 tokens).",
    deferred: false,
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "The numeric ID of the feature.",
        },
      },
      required: ["featureId"],
    },
    argOrder: ["featureId"],
  },

  // ── Summary by GUID ───────────────────────────────────────────────────
  {
    method: "getSpatialSummaryByGuid",
    name: "WS-spatialDataApi-getSpatialSummaryByGuid",
    description:
      "Use this tool for selected-feature length, area, perimeter, size, extent, bounds, geometry, or point-count questions when only a GUID is available. " +
      "Read the selected feature guid from get_run_context and pass it here; this queries the current Quadri model rather than documentation. " +
      "Returns a compact markdown summary of all spatial attributes for the feature. " +
      "Includes geometry type, point counts, lengths/areas, and bounding box.",
    parameters: {
      type: "object",
      properties: {
        guidString: {
          type: "string",
          description: "The GUID of the feature.",
        },
      },
      required: ["guidString"],
    },
    argOrder: ["guidString"],
  },

  // ── Full spatial data by ID ───────────────────────────────────────────
  {
    method: "getSpatialData",
    name: "WS-spatialDataApi-getSpatialData",
    description:
      "Returns an exact, paginated slice of spatial coordinates in compact CSV-in-markdown format for a feature identified by its numeric ID. " +
      "Response includes totalCount, offset, count, and a next-offset hint. Use offset/limit to page through all coordinates.",
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "The numeric ID of the feature.",
        },
        offset: {
          type: "number",
          description: "Zero-based coordinate offset.",
        },
        limit: {
          type: "number",
          description: "Maximum coordinates to return (1-200).",
        },
      },
      required: ["featureId", "offset", "limit"],
    },
    argOrder: ["featureId", "offset", "limit"],
  },

  // ── Full spatial data by GUID ─────────────────────────────────────────
  {
    method: "getSpatialDataByGuid",
    name: "WS-spatialDataApi-getSpatialDataByGuid",
    description:
      "Returns an exact, paginated slice of spatial coordinates in compact CSV-in-markdown format for a feature identified by its GUID. " +
      "Response includes totalCount, offset, count, and a next-offset hint.",
    parameters: {
      type: "object",
      properties: {
        guidString: {
          type: "string",
          description: "The GUID of the feature.",
        },
        offset: {
          type: "number",
          description: "Zero-based coordinate offset.",
        },
        limit: {
          type: "number",
          description: "Maximum coordinates to return (1-200).",
        },
      },
      required: ["guidString", "offset", "limit"],
    },
    argOrder: ["guidString", "offset", "limit"],
  },

  // ── Design curve tessellated data ─────────────────────────────────────
  {
    method: "getDesignCurveData",
    name: "WS-spatialDataApi-getDesignCurveData",
    description:
      "Returns design curve geometry tessellated at the specified interval for a feature identified by its numeric ID. " +
      "distanceBetweenPoints controls tessellation density (meters between points). " +
      "Use offset/limit to page through the tessellated coordinates without simplifying the result.",
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "The numeric ID of the feature.",
        },
        distanceBetweenPoints: {
          type: "number",
          description:
            "Tessellation interval in meters. Controls how densely the curve is sampled.",
        },
        offset: {
          type: "number",
          description: "Zero-based coordinate offset.",
        },
        limit: {
          type: "number",
          description: "Maximum coordinates to return (1-200).",
        },
      },
      required: ["featureId", "distanceBetweenPoints", "offset", "limit"],
    },
    argOrder: ["featureId", "distanceBetweenPoints", "offset", "limit"],
  },

  // ── General feature slope analysis ───────────────────────────────────
  {
    method: "analyzeFeatureSlopes",
    name: "WS-spatialDataApi-analyzeFeatureSlopes",
    description:
      "Use this general tool to find and rank steep 3D geometry on any selected feature, regardless of feature type or owning task. " +
      "It measures each connected 3D segment and returns gradient percent, horizontal-to-vertical slope ratio, rise or drop, horizontal run, azimuth, and midpoint XYZ. " +
      "Read featureId from get_run_context. Pass minimumGradientPercent=0 to find the steepest segments, or use a threshold explicitly supplied by the user. " +
      "Results are paginated and sorted steepest first. For surfaces, available geometry may contain boundaries rather than interior triangles, so describe the result as measured feature geometry rather than the complete surface slope.",
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "Numeric ID of any selected feature with 3D geometry.",
        },
        minimumGradientPercent: {
          type: "number",
          description:
            "Minimum absolute gradient percent. Use 0 to rank and report the steepest measured segments.",
        },
        offset: {
          type: "number",
          description: "Zero-based result offset.",
        },
        limit: {
          type: "number",
          description: "Maximum results to return (1-200).",
        },
      },
      required: ["featureId", "minimumGradientPercent", "offset", "limit"],
    },
    argOrder: ["featureId", "minimumGradientPercent", "offset", "limit"],
  },

  // ── Roadside slope screening ─────────────────────────────────────────
  {
    method: "analyzeRoadsideSlopes",
    name: "WS-spatialDataApi-analyzeRoadsideSlopes",
    description:
      "Use this tool to find steep fill or embankment locations along a road, or to screen where steep gradients outside a selected shoulder may warrant railing review. " +
      "Read contextFeatureId from the selected Road task result feature in get_run_context. The host infers its owning Road task, analyzes same-side EmbankmentSlope surfaces, and projects transverse slope segments to reference-alignment chainage. " +
      "Pass minimumGradientPercent=0 when the user asks for the steepest location without specifying a threshold. For threshold screening, use only a threshold supplied by the user or an authoritative standard retrieved separately. " +
      "Results are paginated and sorted steepest first. They are geometric screening evidence, not a railing compliance decision; state that limitation and do not invent a regulatory threshold.",
    parameters: {
      type: "object",
      properties: {
        contextFeatureId: {
          type: "number",
          description:
            "Numeric ID of a selected feature owned by the Road task, such as RoadShoulder or EmbankmentSlope.",
        },
        minimumGradientPercent: {
          type: "number",
          description:
            "Minimum absolute transverse gradient percent. Use 0 only to rank and report the steepest measured candidates.",
        },
        offset: {
          type: "number",
          description: "Zero-based candidate offset.",
        },
        limit: {
          type: "number",
          description: "Maximum candidates to return (1-200).",
        },
      },
      required: [
        "contextFeatureId",
        "minimumGradientPercent",
        "offset",
        "limit",
      ],
    },
    argOrder: [
      "contextFeatureId",
      "minimumGradientPercent",
      "offset",
      "limit",
    ],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const spatialDataApi = api?.spatialDataApi;
      if (!api || !spatialDataApi) {
        return JSON.stringify({
          success: false,
          error:
            "SpatialDataApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = spatialDataApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: spatialDataApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name] ?? null);
      const raw = await invokeCefSharpMethod<string>(
        "spatialDataApi",
        spatialDataApi,
        method,
        positionalArgs,
      );

      // SpatialDataApi returns markdown strings, not JSON
      // Return as-is wrapped in a success envelope
      return JSON.stringify({ success: true, data: raw });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every SpatialDataApi method.
 * Call this after `CefSharp.BindObjectAsync("spatialDataApi")` has resolved.
 */
export function createSpatialDataApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: spec.deferred ?? true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
